import { useEffect, useState } from "react"
import { Helmet } from "react-helmet-async"
import { Link, useNavigate } from "react-router-dom"
import { supabase } from "../../lib/supabaseClient"
import { H2, Paragraph, SmallText } from "../../components/typography/Typography"
import { Button } from "../../components/ui-blocks/Button"

export const ResetPassword = () => {
  const navigate = useNavigate()

  const [loading, setLoading] = useState(false)
  const [isRecovery, setIsRecovery] = useState(false)

  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")

  const [error, setError] = useState("")
  const [message, setMessage] = useState("")

  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY") {
        setIsRecovery(true)
        setError("")
        setMessage("")
      }
    })

    const checkSession = async () => {
      const { data } = await supabase.auth.getSession()
      if (data.session) {
        setIsRecovery(true)
      }
    }

    checkSession()

    return () => {
      subscription.unsubscribe()
    }
  }, [])

  const onSendResetLink = async () => {
    setError("")
    setMessage("")

    if (!email.trim()) {
      setError("Please enter your email address.")
      return
    }

    setLoading(true)

    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    })

    if (error) {
      setError("There was an error sending the reset link. Please try again.")
    } else {
      setMessage("A password reset link has been sent to your email address.")
    }

    setLoading(false)
  }

  const onUpdatePassword = async () => {
    setError("")
    setMessage("")

    if (!password || !confirmPassword) {
      setError("Please fill in both password fields.")
      return
    }

    if (password.length < 6) {
      setError("Password must be at least 6 characters long.")
      return
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match.")
      return
    }

    setLoading(true)

    const { error } = await supabase.auth.updateUser({
      password,
    })

    if (error) {
      setError("There was an error updating your password. Please try again.")
      setLoading(false)
      return
    }

    setMessage("Your password has been updated successfully.")
    setLoading(false)

    setTimeout(() => {
      navigate("/login")
    }, 1200)
  }

  return (
    <>
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
        <title>Reset Password | XtractPro Seating</title>
      </Helmet>

      <div className="min-h-[70vh] flex flex-col gap-4 items-center justify-center bg-gray-50 px-4">
        <div className="w-full max-w-md rounded-xl bg-white p-8 shadow-sm border border-gray-200">
          <H2 className="text-center">
            {isRecovery ? "Set New Password" : "Reset Password"}
          </H2>

          <Paragraph className="mt-2 text-center text-gray-600">
            {isRecovery
              ? "Enter your new password below."
              : "Enter your email and we’ll send you a password reset link."}
          </Paragraph>

          <div className="mt-8 flex flex-col gap-4">
            {!isRecovery ? (
              <>
                <input
                  type="email"
                  placeholder="Enter email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value)
                  }}
                  className="w-full rounded-md border border-gray-300 px-4 py-3 text-base focus:border-my-orange focus:outline-none focus:ring-1 focus:ring-my-orange"
                />

                <Button variant="primary" onClick={onSendResetLink} disabled={loading}>
                  {loading ? "Sending..." : "Send Reset Link"}
                </Button>
              </>
            ) : (
              <>
                <input
                  type="password"
                  placeholder="New password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value)
                  }}
                  className="w-full rounded-md border border-gray-300 px-4 py-3 text-base focus:border-my-orange focus:outline-none focus:ring-1 focus:ring-my-orange"
                />

                <input
                  type="password"
                  placeholder="Confirm password"
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value)
                  }}
                  className="w-full rounded-md border border-gray-300 px-4 py-3 text-base focus:border-my-orange focus:outline-none focus:ring-1 focus:ring-my-orange"
                />

                <Button variant="primary" onClick={onUpdatePassword} disabled={loading}>
                  {loading ? "Updating..." : "Update Password"}
                </Button>
              </>
            )}

            {error && <SmallText className="text-red-500">{error}</SmallText>}
            {message && <SmallText className="text-green-600">{message}</SmallText>}

            <Link to="/login" className="text-blue-500">
              Back to Login
            </Link>
          </div>
        </div>
      </div>
    </>
  )
}